import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Readiness() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [criteria, setCriteria] = useState([]);
  const [checked, setChecked] = useState({});
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/readiness-criteria").then((r) => setCriteria(r.data.criteria));
  }, []);

  const allChecked = criteria.length > 0 && criteria.every((_, i) => checked[i]);
  const isResilience = user?.current_phase === "skin_resilience";

  const setPhase = async (target) => {
    setSaving(true); setErr("");
    try {
      await api.put("/profile", { current_phase: target });
      await refresh();
      nav("/dashboard", { replace: true });
    } catch (e) { setErr(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="App" data-testid="page-readiness">
      <NavBar />
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
        <div className="aa-eyebrow mb-3">Phase gate</div>
        <h1 className="font-display text-4xl lg:text-5xl leading-tight">{isResilience ? "Am I ready for actives?" : "Manage your phase"}</h1>
        <p className="mt-4 text-[var(--aa-text-2)]">
          {isResilience
            ? "Before moving to Making Actives Count, your barrier should be quiet and consistent. Check each statement that's true for you right now — all must be checked."
            : "If your skin has been struggling, returning to Skin Resilience can be the right move."}
        </p>

        {isResilience && (
          <div className="aa-card p-7 mt-8 space-y-4" data-testid="readiness-list">
            {criteria.map((c, i) => (
              <label key={i} className="flex gap-3 items-start cursor-pointer">
                <input type="checkbox" data-testid={`readiness-${i}`} checked={!!checked[i]} onChange={(e) => setChecked({ ...checked, [i]: e.target.checked })}
                  className="mt-1 h-5 w-5 shrink-0 accent-[var(--aa-text)]" style={{ minWidth: "1.25rem", minHeight: "1.25rem" }} />
                <span className="text-[var(--aa-text-2)]">{c}</span>
              </label>
            ))}
          </div>
        )}

        {err && <div className="text-sm text-[var(--aa-terra)] mt-4" data-testid="readiness-error">{err}</div>}

        <div className="flex flex-wrap justify-between gap-4 mt-8">
          {isResilience ? (
            <>
              <div className="aa-eyebrow self-center">{allChecked ? "All set — ready to phase up." : "All five must be checked."}</div>
              <button data-testid="readiness-advance" disabled={!allChecked || saving} onClick={() => setPhase("making_actives_count")}
                className="aa-cta-primary">
                {saving ? "Updating…" : "Begin Making Actives Count →"}
              </button>
            </>
          ) : (
            <>
              <div className="aa-eyebrow self-center">Currently on {user?.current_phase === "making_actives_count" ? "Making Actives Count" : "Precision Corrections"}.</div>
              <button data-testid="readiness-revert" onClick={() => setPhase("skin_resilience")} disabled={saving} className="aa-cta-secondary">
                Return to Skin Resilience
              </button>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
